/**
 * §5.7 認證稽核。需要 `authaudit.read`。
 *
 * 跟「稽核軌跡」（/audit）分開的理由：那一頁是**業務事件**（進庫、領料、繳庫），
 * 這一頁是**誰動了帳號與權限**。稽核來的時候問的是不同的問題：
 * 「這批料誰處理的」看 /audit，「這個人為什麼有這個權限」看這裡。
 *
 * §11.5 下載屬於資料外流，後端會把下載這件事本身也記成一筆 `export` 事件——
 * 所以你在清單裡看得到自己剛剛下載過什麼。那是刻意的。
 */

import { useState } from 'react'
import { download, withQuery } from '../../api/client.js'
import { useQuery } from '../../api/hooks.js'
import { Button, Card, Empty, Mono, Pill, Select, Table, Td, TextInput } from '../../components/ui.js'
import { ts } from '../../../../shared/lib/format.js'

interface LogRow {
  id: string
  at: string
  kind: string
  user_id: number | null
  display_name: string | null
  email: string | null
  actor_user_id: number | null
  actor_display_name: string | null
  email_tried: string | null
  site_id: string | null
  ip: string | null
  user_agent: string | null
  detail: Record<string, unknown> | null
}

/** §5.7 的事件種類。分組是為了讓下拉選單找得到，不是資料庫裡的分類。 */
const KIND_GROUPS: { label: string; kinds: [string, string][] }[] = [
  {
    label: '登入',
    kinds: [
      ['login_ok', '登入成功'],
      ['login_fail', '登入失敗'],
      ['locked', '帳號鎖定'],
      ['logout', '登出'],
      ['code_requested', '索取驗證碼'],
      ['code_ok', '驗證碼登入成功'],
      ['code_fail', '驗證碼錯誤'],
      ['password_changed', '自行更改密碼'],
    ],
  },
  {
    label: '帳號與角色',
    kinds: [
      ['account_created', '建立帳號'],
      ['account_updated', '修改帳號'],
      ['account_disabled', '停用帳號'],
      ['account_enabled', '重新啟用帳號'],
      ['password_reset', '管理者重設密碼'],
      ['account_unlocked', '解除鎖定'],
      ['role_changed', '變更某人的角色'],
      ['role_created', '建立角色'],
      ['role_permissions_changed', '變更角色的權限'],
      ['role_deleted', '刪除角色'],
      ['session_revoked', '強制登出'],
    ],
  },
  {
    label: '廠區',
    kinds: [
      ['site_created', '建立廠區'],
      ['site_updated', '修改廠區'],
      ['site_status_changed', '變更廠區狀態'],
      ['site_deleted', '刪除廠區'],
      ['site_switched', '切換廠區'],
    ],
  },
  {
    label: '系統',
    kinds: [
      ['smtp_updated', '變更寄信設定'],
      ['smtp_tested', '寄送測試信'],
      ['auth_settings_changed', '變更登入方式'],
      ['device_registered', '註冊設備'],
      ['device_updated', '修改設備'],
      ['device_secret_rotated', '輪替設備金鑰'],
      ['export', '下載資料'],
    ],
  },
]

const KIND_LABEL: Record<string, string> = Object.fromEntries(
  KIND_GROUPS.flatMap((g) => g.kinds),
)

/**
 * 值得在清單上標色的幾種。
 * 判準是「稽核來的時候會先問這一筆」——權限變動、資料外流、以及失敗的登入嘗試。
 */
const TONE: Record<string, 'red' | 'amber' | 'violet'> = {
  login_fail: 'amber',
  code_fail: 'amber',
  locked: 'red',
  password_reset: 'amber',
  role_changed: 'violet',
  role_created: 'violet',
  role_permissions_changed: 'violet',
  role_deleted: 'red',
  session_revoked: 'amber',
  account_disabled: 'amber',
  site_deleted: 'red',
  auth_settings_changed: 'violet',
  smtp_updated: 'violet',
  export: 'violet',
}

export function AdminAuthLog() {
  const [kind, setKind] = useState('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [pages, setPages] = useState<LogRow[][]>([])
  const [cursor, setCursor] = useState<string | null>(null)

  const params = {
    kind: kind || undefined,
    from: from ? new Date(from).toISOString() : undefined,
    // 結束日期要含當天，所以往後推一天（後端是 e.at < $4）
    to: to ? new Date(new Date(to).getTime() + 86_400_000).toISOString() : undefined,
    cursor, limit: 50,
  }
  const { data, error, loading } = useQuery<{ items: LogRow[]; nextCursor: string | null }>(
    withQuery('/api/admin/auth-log', params),
  )
  const items = [...pages.flat(), ...(data?.items ?? [])]

  const resetPaging = (fn: () => void) => { setPages([]); setCursor(null); fn() }

  return (
    <Card
      title="認證稽核"
      subtitle="誰動了帳號、角色、廠區與系統設定。作業本身的紀錄在「稽核軌跡」"
      right={
        <Button size="sm"
          onClick={() => download('/api/admin/auth-log', { ...params, cursor: undefined, format: 'csv' })}>
          下載 CSV
        </Button>
      }
    >
      <div className="mb-3 flex flex-wrap items-end gap-2">
        <Select value={kind} onChange={(e) => resetPaging(() => setKind(e.target.value))}>
          <option value="">全部動作</option>
          {KIND_GROUPS.map((g) => (
            <optgroup key={g.label} label={g.label}>
              {g.kinds.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
            </optgroup>
          ))}
        </Select>
        <TextInput type="date" value={from} className="max-w-[160px]"
          onChange={(e) => resetPaging(() => setFrom(e.target.value))} />
        <span className="text-sm text-ink-400">→</span>
        <TextInput type="date" value={to} className="max-w-[160px]"
          onChange={(e) => resetPaging(() => setTo(e.target.value))} />
        {(kind || from || to) && (
          <Button size="sm" variant="ghost"
            onClick={() => resetPaging(() => { setKind(''); setFrom(''); setTo('') })}>清除條件</Button>
        )}
      </div>

      {error && <p className="text-sm text-red-700">{error.message}</p>}
      {items.length === 0 && !loading && <Empty>這個範圍內沒有紀錄。</Empty>}

      {items.length > 0 && (
        <Table head={['時間', '動作', '對象', '執行者', '廠區', '來源', '細節']} dense>
          {items.map((e) => {
            const tone = TONE[e.kind]
            return (
              <tr key={e.id} className={tone === 'red' ? 'bg-red-50' : tone === 'amber' ? 'bg-amber-50' : ''}>
                <Td className="text-xs whitespace-nowrap">{ts(e.at)}</Td>
                <Td className="text-xs">
                  {tone
                    ? <Pill tone={tone}>{KIND_LABEL[e.kind] ?? e.kind}</Pill>
                    : (KIND_LABEL[e.kind] ?? e.kind)}
                </Td>
                <Td className="text-xs">
                  {/* email_tried 是「登入失敗時對方打的那個帳號」——可能根本不存在，
                      那本身就是重要資訊，所以要顯示出來而不是留白 */}
                  {e.display_name ?? (e.email_tried
                    ? <span className="text-ink-500">{e.email_tried}<span className="ml-1 text-[11px] text-amber-700">（嘗試）</span></span>
                    : <span className="text-ink-300">—</span>)}
                </Td>
                <Td className="text-xs">{e.actor_display_name ?? <span className="text-ink-300">—</span>}</Td>
                <Td className="text-xs">{e.site_id ?? <span className="text-ink-300">—</span>}</Td>
                <Td className="text-xs"><Mono className="text-[11px]">{e.ip ?? '—'}</Mono></Td>
                <Td className="text-xs text-ink-600 max-w-[280px]"><Detail kind={e.kind} detail={e.detail} /></Td>
              </tr>
            )
          })}
        </Table>
      )}

      {data?.nextCursor && (
        <div className="mt-3 text-center">
          <Button size="sm" disabled={loading}
            onClick={() => { setPages((p) => [...p, data.items]); setCursor(data.nextCursor) }}>
            {loading ? '載入中…' : '載入更多'}
          </Button>
        </div>
      )}
    </Card>
  )
}

/**
 * 一行摘要。刻意不倒整包 JSON——那個看不出重點，而且 detail 的形狀每種事件都不一樣。
 * 認不得的種類就退回 JSON，總比空白好。
 */
function Detail({ kind, detail }: { kind: string; detail: Record<string, unknown> | null }) {
  if (!detail) return <span className="text-ink-300">—</span>
  const d = detail as Record<string, any>

  switch (kind) {
    case 'login_fail':
      return <>{d.reason === 'bad_password' ? '密碼錯誤' : d.reason === 'no_user' ? '帳號不存在'
        : d.reason === 'disabled' ? '帳號已停用' : d.reason ?? '—'}</>
    case 'locked':
      return <>連續失敗 {d.attempts ?? '?'} 次，鎖到 {d.until ? ts(d.until) : '—'}</>
    case 'role_changed':
      return <>{(d.after ?? []).map((g: any) => `${g.siteId === '*' ? '全廠區' : g.siteId}:${g.roleCode}`).join('、') || '（清空）'}</>
    case 'role_created':
    case 'role_deleted':
      return <>角色 <Mono>{d.code}</Mono>（{d.name}） · {(d.permissions ?? []).length} 項權限</>
    case 'role_permissions_changed':
      return <>
        {d.added?.length ? `＋${d.added.length}` : ''}
        {d.added?.length && d.removed?.length ? ' ' : ''}
        {d.removed?.length ? `−${d.removed.length}` : ''}
        {!d.added?.length && !d.removed?.length ? '無變動' : ' 項權限'}
      </>
    case 'account_created':
      return <>{d.email}{d.via === 'BOOTSTRAP_ADMIN_EMAIL' ? '（系統初始化）' : ''}</>
    case 'site_status_changed':
      return <>{d.before} → <strong>{d.after}</strong></>
    case 'site_created':
    case 'site_updated':
    case 'site_deleted':
      return <><Mono>{d.siteId ?? d.site_id}</Mono> {d.name ?? ''}</>
    case 'smtp_tested':
      return <>寄到 {d.to} · {d.ok ? '成功' : <span className="text-red-700">失敗：{String(d.error ?? '').slice(0, 60)}</span>}</>
    case 'smtp_updated':
      return <>{d.emailCodeDisabled ? '連線設定變更，Email 驗證碼已自動關閉' : '寄件者資訊更新'}</>
    case 'auth_settings_changed':
      return <>密碼 {d.after?.password ? '開' : '關'} · 驗證碼 {d.after?.emailCode ? '開' : '關'}</>
    case 'session_revoked':
      return <>工作階段 <Mono className="text-[11px]">{String(d.sessionId ?? '').slice(0, 8)}…</Mono></>
    case 'export':
      return <>{d.report} · {d.rows} 筆 · {d.format}</>
    case 'site_switched':
      return <>{d.from ?? '—'} → <strong>{d.to ?? '—'}</strong></>
    default: {
      const s = JSON.stringify(d)
      return <span className="text-[11px] text-ink-400">{s.length > 90 ? `${s.slice(0, 90)}…` : s}</span>
    }
  }
}
